import {
  createLocation,
  deleteLocation,
  fetchFleetLocations,
  updateLocation,
  type LocationInsertDTO,
  type LocationUpdateDTO,
} from "@/api/vtnz-backend/locations";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import SavedLocation, { isSavedLocationType } from "./savedLocation";

export const useSavedLocationStore = defineStore("savedLocationStore", () => {
  // ---------------------------------- State --------------------------------- //
  const savedLocations = ref<SavedLocation[]>([]);
  const fetchState = ref<FetchState>("idle");
  const fetchErrorMsg = ref<string | null>(null);

  // --------------------------------- Getters -------------------------------- //

  const getSavedLocations = computed<SavedLocation[]>(() => savedLocations.value);

  const isFetching = computed(() => fetchState.value === "loading");

  function getLocationById(id: string): SavedLocation | undefined {
    return getSavedLocations.value.find((location) => location.localId === id);
  }

  /**
   * Looks up a saved location by its external id - useful for translating a
   * backend-sourced id (e.g. a vehicle's `home_location_id`) into the
   * location, since `getLocationById` above keys off the local id instead.
   */
  function getLocationByExternalId(
    externalId: string,
  ): SavedLocation | undefined {
    return getSavedLocations.value.find(
      (location) => location.externalId === externalId,
    );
  }

  // --------------------------------- Actions -------------------------------- //

  async function fetchLocations(): Promise<void> {
    fetchState.value = "loading";
    fetchErrorMsg.value = null;

    try {
      const response = await fetchFleetLocations();

      if (!response.success) {
        fetchErrorMsg.value = response.errorMsg;
        fetchState.value = "error";
        return;
      }

      const fetchedLocations: SavedLocation[] = [];
      const errors: string[] = [];

      for (const dto of response.data ?? []) {
        try {
          fetchedLocations.push(SavedLocation.fromDTO(dto));
        } catch (error) {
          errors.push(
            error instanceof Error
              ? error.message
              : `Failed to create location ${dto.id}`,
          );
        }
      }

      savedLocations.value = fetchedLocations;

      if (errors.length > 0) {
        console.warn("Some locations could not be loaded", errors);
      }

      fetchState.value = "success";
    } catch (error) {
      fetchErrorMsg.value =
        error instanceof Error
          ? error.message
          : "Failed to fetch saved locations";

      fetchState.value = "error";
    }
  }

  async function deleteSavedLocation(
    locationId: string,
  ): Promise<DeleteSavedLocationResult> {
    const targetLocation = getLocationById(locationId);

    if (!targetLocation) {
      return {
        success: false,
        status: "NO-LOCATION",
        errorMsg: "Could not find location to delete",
      };
    }

    if (!targetLocation.externalId) {
      // This location only exists in local state.

      savedLocations.value = savedLocations.value.filter(
        (location) => location.localId !== locationId,
      );

      return {
        success: true,
        status: "REMOVED-LOCALLY",
      };
    }

    const res = await deleteLocation(targetLocation.externalId);

    if (res.success) {
      savedLocations.value = savedLocations.value.filter(
        (location) => location.localId !== locationId,
      );

      return {
        success: true,
        status: "DELETED",
      };
    }

    return {
      success: false,
      status: "FAILED-TO-DELETE",
      errorMsg: res.errorMsg,
    };
  }

  async function createSavedLocation(
    locationData: LocationInsertDTO,
  ): Promise<{ success: false; errorMsg: string } | { success: true }> {
    const res = await createLocation(locationData);

    if (!res.success) {
      return {
        success: false,
        errorMsg: res.errorMsg,
      };
    }

    const location = SavedLocation.fromDTO(res.data);

    savedLocations.value.push(location);

    return {
      success: true,
    };
  }

  async function updateSavedLocation(
    locationId: string,
    locationData: LocationUpdateDTO,
  ): Promise<UpdateSavedLocationResult> {
    const targetLocation = getLocationById(locationId);

    if (!targetLocation) {
      return {
        success: false,
        status: "NO-LOCATION",
        errorMsg: "Could not find location to update",
      };
    }

    if (!targetLocation.externalId) {
      return {
        success: false,
        status: "NOT-PERSISTED",
        errorMsg: "This location has not been persisted and cannot be updated",
      };
    }

    const res = await updateLocation(targetLocation.externalId, locationData);

    if (!res.success) {
      return {
        success: false,
        status: "FAILED-TO-UPDATE",
        errorMsg: res.errorMsg,
      };
    }

    // Persist state only after the API confirms the update.
    if (locationData.address) {
      targetLocation.address = locationData.address;
    }
    if (locationData.latitude != null) {
      targetLocation.coordinates.latitude = locationData.latitude;
    }
    if (locationData.longitude != null) {
      targetLocation.coordinates.longitude = locationData.longitude;
    }
    if (locationData.name) {
      targetLocation.name = locationData.name;
    }
    if (locationData.type && isSavedLocationType(locationData.type)) {
      targetLocation.type = locationData.type;
    }

    return {
      success: true,
      status: "UPDATED",
    };
  }

  function $reset() {
    fetchState.value = "idle";
    savedLocations.value = [];
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** indicates the stores current data fetching state */
    fetchState,

    /** error message for last fetching error if there is one. */
    fetchErrorMsg,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicated async fetching is in operation. Good for loading animations. */
    isFetching,

    /** Returns all the saved locations the app knows about. */
    getSavedLocations,

    /**Returns the full location object for the passed location id. */
    getLocationById,

    /** Looks up a saved location by its external id. */
    getLocationByExternalId,

    // -------------------------------- Actions ------------------------------- //

    /** Fetches the fleets saved locations from the backend and assigns it to sate. */
    fetchLocations,

    /** Creates a saved location in both backend and state */
    createSavedLocation,

    /** Deletes the target location from state and the backend. */
    deleteSavedLocation,

    /** Updates the target location in state and the backend. */
    updateSavedLocation,

    /** Resets the store to its initial state. */
    $reset,
  };
});

type DeleteSavedLocationResult =
  | {
      success: false;
      status: "NO-LOCATION";
      errorMsg: string;
    }
  | {
      success: true;
      status: "REMOVED-LOCALLY";
    }
  | {
      success: true;
      status: "DELETED";
    }
  | {
      success: false;
      status: "FAILED-TO-DELETE";
      errorMsg: string;
    };

type UpdateSavedLocationResult =
  | {
      success: true;
      status: "UPDATED";
    }
  | {
      success: false;
      status: "NO-LOCATION";
      errorMsg: string;
    }
  | {
      success: false;
      status: "NOT-PERSISTED";
      errorMsg: string;
    }
  | {
      success: false;
      status: "FAILED-TO-UPDATE";
      errorMsg: string;
    };
